import type { BlueprintState, CandidateUseCase, PainPoint } from "../api/types";

export class BlueprintPane {
  private container: HTMLElement;
  private _localState: BlueprintState = {
    company_profile: null,
    pain_points: [],
    candidate_use_cases: [],
    systems: [],
  };

  constructor(container: HTMLElement) {
    this.container = container;
    this.render(this._localState);
  }

  update(state: BlueprintState): void {
    // Merge incoming snapshot into local accumulated state — never replace with empty
    if (state.company_profile) {
      this._localState.company_profile = state.company_profile;
      this._setText(".bp-card-profile", state.company_profile);
    }
    if (state.pain_points.length > 0) {
      this._localState.pain_points = state.pain_points;
      this._renderPainPoints(state.pain_points);
    }
    if (state.candidate_use_cases.length > 0) {
      this._localState.candidate_use_cases = state.candidate_use_cases;
      this._renderUseCases(state.candidate_use_cases);
    }
    if (state.systems.length > 0) {
      // Union: add new systems without losing previously detected ones
      const merged = Array.from(new Set([...this._localState.systems, ...state.systems]));
      this._localState.systems = merged;
      this._renderSystems(merged);
    }
  }

  private render(state: BlueprintState): void {
    this.container.innerHTML = `
      <div class="pane-label">
        <span>Your blueprint — building live</span>
        <span class="live-dot" aria-live="polite" aria-label="Updating in real time">Updating</span>
      </div>

      <div class="bp-card">
        <div class="bp-card-label">Company profile</div>
        <div class="bp-card-content bp-card-profile" style="color:var(--swo-ash);">${state.company_profile ?? "Listening..."}</div>
      </div>

      <div class="bp-card">
        <div class="bp-card-label">Pain points identified</div>
        <div class="bp-bullets bp-bullets-pain">
          <div class="bp-bullet" style="color:var(--swo-ash);opacity:0.6;">Listening for what slows the team down...</div>
        </div>
      </div>

      <div class="bp-card">
        <div class="bp-card-label">Candidate use cases</div>
        <div class="bp-use-cases">
          <div class="uc-card dashed">
            <div class="uc-head">
              <span class="uc-name" style="color:var(--swo-granite);font-weight:normal;">Will appear as we talk...</span>
            </div>
          </div>
        </div>
      </div>

      <div class="bp-card">
        <div class="bp-card-label">Data &amp; systems</div>
        <div class="tags bp-systems">
          <span class="tag dashed">Detecting systems...</span>
        </div>
      </div>
    `;
  }

  private _setText(selector: string, text: string): void {
    const el = this.container.querySelector<HTMLElement>(selector);
    if (el) el.textContent = text;
  }

  private _renderPainPoints(points: PainPoint[]): void {
    const el = this.container.querySelector<HTMLElement>(".bp-bullets-pain");
    if (!el) return;
    el.innerHTML = points
      .map((p) => `<div class="bp-bullet">${p.label}${p.detail ? `: ${p.detail}` : ""}</div>`)
      .join("");
  }

  private _renderUseCases(useCases: CandidateUseCase[]): void {
    const el = this.container.querySelector<HTMLElement>(".bp-use-cases");
    if (!el) return;
    el.innerHTML = useCases
      .map(
        (uc) => `
        <div class="uc-card${uc.is_parked ? " dashed" : ""}">
          <div class="uc-head">
            <span class="uc-name">${uc.name}</span>
            <span class="uc-fit ${uc.fit_score === 'medium' ? 'med' : uc.fit_score}">${uc.fit_score.charAt(0).toUpperCase() + uc.fit_score.slice(1)} fit</span>
          </div>
          ${uc.est_impact ? `<div class="uc-meta">${uc.est_impact}</div>` : ""}
        </div>
      `,
      )
      .join("");
  }

  private _renderSystems(systems: string[]): void {
    const el = this.container.querySelector<HTMLElement>(".bp-systems");
    if (!el) return;
    el.innerHTML = systems.map((s) => `<span class="tag">${s}</span>`).join("");
  }
}
